import { useEffect, useMemo, useState } from 'react'
import Avatar from 'react-avatar'
import Table from '../../../../UI/Table'
import MiniHeader from '../../../../UI/MiniHeader'
import { useTranslation } from 'react-i18next'

import styles from './style.module.scss'
import { StateOptions, TApi, TRoles } from '../../../../shared/const'
import Btn from '../../../../UI/Btn'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import VisibilityAccess from '../../../VisibilityAccess'
import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import BtnTooltip from '../../../../UI/BtnTooltip'
import AuthAvatar from '../../../../UI/AuthAvatar'
import Overview from './Overview'
import { filterAdmins } from './stats'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { formatVienna } from '../../../../shared/datetime'
import { REMINDER_KINDS, reminderShortLabel } from '../../../../shared/reminderSettings'

// Die Administratoren sind eine überschaubare, langsam wachsende Gruppe
// (zweistellige Anzahl). Sie stehen deshalb in einer einzigen Liste, ohne
// Blättern — sonst müsste man für „wie viele sind es?" durch Seiten klicken.
// Die Obergrenze schützt nur vor einer entgleisten Abfrage.
const LIMIT = 500

function Admins(props) {
	const permissions = props.permissions

	const { t } = useTranslation()
	const [admins, setAdmins] = useState([])
	const [sort, setSort] = useState({ name: 'state', direction: 'asc' })
	// Auswahl aus den Balken der Übersicht. Beides gilt zusammen, beides ist
	// einzeln abschaltbar.
	const [filter, setFilter] = useState({ role: null, state: null })

	const arePermissionsLoaded = Boolean(permissions)

	const fetchAdmins = () => {
		http.get(TApi.ADMIN_STATE_ADMINS, {
			params: {
				limit: LIMIT,
				offset: 0,
				direction: sort.direction,
				order: sort.name
			}
		}).then(({ data }) => {
			setAdmins(data.rows)
		})
	}

	useEffect(() => {
		if (arePermissionsLoaded) {
			fetchAdmins()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sort, arePermissionsLoaded])

	const handleDeleteAdminWithConfirmation = async id => {
		const { isConfirmed } = await Swal.fire({
			title: t('Are you sure?'),
			text: t('You will not be able to revert this!'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#0CA789',
			cancelButtonColor: '#d33',
			confirmButtonText: t('Yes, delete it!'),
			cancelButtonText: t('Cancel')
		})

		if (!isConfirmed) return

		toast.info(t('Deleting admin...'))
		try {
			// `disableToast`: Der eigene Satz benennt den Vorgang, der schiefging.
			await http.delete(TApi.ADMIN_STATE_ADMINS_ID(id), { disableToast: true })
			toast.success(t('Admin was successfully deleted'))
			fetchAdmins()
		} catch (error) {
			notifyError(error, 'Failed to delete admin. Please try again later.')
		}
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	// Derselbe Balken noch einmal angeklickt nimmt seinen Filter wieder zurück —
	// so kommt man ohne Umweg über den Zurücksetzen-Knopf wieder heraus.
	const handleToggleFilter = (kind, value) =>
		setFilter(current => ({ ...current, [kind]: current[kind] === value ? null : value }))

	const handleResetFilter = () => setFilter({ role: null, state: null })

	const isFiltering = Boolean(filter.role || filter.state)

	const visibleAdmins = useMemo(() => filterAdmins(admins, filter), [admins, filter])

	const handleDone = () => fetchAdmins()

	// Landesadmins und Bundesadmins sind zwei getrennte Berechtigungen — wer
	// die eine hat, darf die andere Rolle deshalb noch nicht bearbeiten.
	// Alumni (#213) sind bundesweit — dasselbe Recht wie für Bundesadmins
	// (backend `admins/permissions.js`).
	const canManage = admin =>
		(admin.role === TRoles.ADMIN && Boolean(permissions?.manageStateAdmins)) ||
		([TRoles.FEDERAL_ADMIN, TRoles.ALUMNI].includes(admin.role) && Boolean(permissions?.manageFederalAdmins))

	const canAddAdmin = Boolean(permissions?.manageStateAdmins || permissions?.manageFederalAdmins)

	/*
	 * Öffnet das Fenster eines Kontos. Jede Zeile lässt sich öffnen: Die
	 * Erinnerungen per E-Mail darf jeder Bundesadmin für alle Verwaltungskonten
	 * einstellen, auch ohne das Recht, den Zugang selbst zu bearbeiten. Ohne
	 * dieses Recht zeigt das Fenster nur „Erinnerungen" und „Kommentare".
	 */
	const openAdmin = (admin, tab) =>
		eventEmitter.emit(types.openAddAdmin, admin, handleDone, { canEdit: canManage(admin), tab })

	const columns = useMemo(
		() =>
			arePermissionsLoaded
				? [
						{
							// Die Liste beginnt mit dem Namen — er trägt den
							// Datensatz (siehe UI/Table).
							Header: 'Name',
							accessor: 'firstName',
							minWidth: 220,
							Cell: ({ row }) => {
								const shouldShowButtons = canManage(row.original)

								const fullName = `${row.original.firstName} ${row.original.lastName}`.trim()

								return (
									<div className={styles.name}>
										{/*
										 * Das Foto steht vor dem Namen — dieselbe
										 * Anordnung wie in der Liste der
										 * Unterstützer:innen. Wer keins hinterlegt
										 * hat, bekommt die Initialen; eine leere
										 * Fläche würde die Namen sonst gegeneinander
										 * versetzen.
										 */}
										<div className={styles.nameCell}>
											<AuthAvatar
												hashName={row.original.avatarHashName}
												alt=""
												className={styles.avatar}
												fallback={
													<Avatar
														name={fullName || '?'}
														size="32"
														round
														color="#c3edef"
														fgColor="#22404d"
													/>
												}
											/>
											<p className={styles.nameText}>{fullName}</p>
										</div>
										{shouldShowButtons && (
											<BtnTooltip
												className={styles.btnToolTip}
												items={[
													{
														label: t('update'),
														onClick: () => openAdmin(row.original)
													},
													{
														className: styles.btnRemove,
														label: t('Remove'),
														onClick: () => {
															handleDeleteAdminWithConfirmation(row.original.id)
														}
													}
												]}
											/>
										)}
									</div>
								)
							}
						},

						{
							Header: t('Role'),
							accessor: 'role',
							minWidth: 140,
							Cell: ({ row }) => {
								const role = row.original.role

								const roleName =
									role === TRoles.ADMIN
										? t('Stateadmin')
										: role === TRoles.FEDERAL_ADMIN
										? t('Federaladmin')
										: role === TRoles.ALUMNI
										? t('ALUMNI')
										: role

								return <p>{roleName}</p>
							}
						},

						{
							Header: t('state'),
							accessor: 'state',
							minWidth: 160,
							Cell: ({ row }) => {
								return (
									<p>
										{row.original.state
											? t(StateOptions.find(({ value }) => value === row.original.state)?.label)
											: t('All')}
									</p>
								)
							}
						},
						{
							Header: t('Organization name'),
							accessor: 'organizationName',
							minWidth: 220,
							Cell: ({ row }) => {
								return <p>{row.original.organizationName}</p>
							}
						},
						{
							Header: t('Administrator email'),
							accessor: 'email',
							minWidth: 240,
							Cell: ({ row }) => {
								return (
									<div className={styles.email}>
										<span>{row.original.email}</span>
									</div>
								)
							}
						},
						{
							/*
							 * Welche Erinnerungen per E-Mail dieses Konto bekommt —
							 * eine Zeile je eingeschalteter Art, ein Strich, wenn
							 * keine. Klick öffnet das Fenster gleich im Reiter
							 * „Erinnerungen".
							 */
							Header: t('Reminders'),
							accessor: 'reminders',
							disableSortBy: true,
							minWidth: 190,
							Cell: ({ row }) => {
								const zeilen = Object.values(REMINDER_KINDS)
									.map(kind => ({
										kind,
										text: reminderShortLabel(row.original.reminders?.[kind], t)
									}))
									.filter(zeile => zeile.text)

								return (
									<button
										type="button"
										className={styles.reminders}
										onClick={() => openAdmin(row.original, 'reminders')}
										title={t('Email reminders')}
									>
										{zeilen.length ? (
											zeilen.map(zeile => (
												<span key={zeile.kind} className={styles.reminderLine}>
													<strong>{t(`reminderShort${zeile.kind}`)}</strong> {zeile.text}
												</span>
											))
										) : (
											<span className={styles.noReminder}>—</span>
										)}
									</button>
								)
							}
						},
						{
							// Zeigt, welche Zugänge tatsächlich benutzt werden.
							// Leer heißt nicht „nie angemeldet", sondern „seit
							// Einführung der Aufzeichnung nicht" — der Hinweis
							// dazu steht in der Übersicht darüber.
							Header: t('Last sign-in'),
							accessor: 'lastLoginAt',
							minWidth: 160,
							// Beim ersten Klick die jüngste Anmeldung oben.
							sortDescFirst: true,
							Cell: ({ row }) => {
								const value = row.original.lastLoginAt

								return (
									<p className={value ? undefined : styles.noLogin}>
										{value ? formatVienna(value, 'dd.MM.yyyy HH:mm') : '—'}
									</p>
								)
							}
						}
				  ]
				: [],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[permissions, t, arePermissionsLoaded]
	)
	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('stateAdmins')}>
				{/*
				 * Anlegen darf nur, wer wenigstens eine der beiden Rollen
				 * verwalten darf. Vorher stand der Knopf bei allen — das
				 * Fenster ließ sich dann aber nicht speichern, weil die Rolle
				 * ein Pflichtfeld ist, das nur mit dem Recht sichtbar wird.
				 *
				 * Die Bedingung steht AUSSERHALB von VisibilityAccess: Dessen
				 * Kinder dürfen nicht `undefined` sein (die Rechte fehlen im
				 * ersten Augenblick nach dem Neuladen noch), sonst gibt es
				 * „Nothing was returned from render".
				 */}
				{canAddAdmin && (
					<VisibilityAccess access={[TRoles.ADMIN, TRoles.FEDERAL_ADMIN]}>
						<Btn
							// 168 px trugen die deutsche Beschriftung nicht: Der
							// Knopf schnitt „Administrator hinzufügen" vorne ab.
							width={230}
							onClick={() => eventEmitter.emit(types.openAddAdmin, null, handleDone)}
							iconSize={{ w: 10, h: 10 }}
							icon="arrow"
							iconColor="white"
							theme="orange"
						>
							{t('Add Administrator')}
						</Btn>
					</VisibilityAccess>
				)}
			</MiniHeader>
			{arePermissionsLoaded ? (
				<>
					{/* In derselben Hülle wie die Tabelle, damit Übersicht und
					    Liste am selben linken Rand beginnen. */}
					<div className="container">
						{/*
						 * Die Übersicht bekommt immer den ganzen Bestand: Ihre
						 * Balken sind die Bedienung selbst und müssten sonst beim
						 * ersten Klick auf die eigene Auswahl zusammenschrumpfen.
						 */}
						<Overview
							admins={admins}
							filter={filter}
							onToggle={handleToggleFilter}
							onReset={handleResetFilter}
							visibleCount={visibleAdmins.length}
						/>
					</div>
					<div className="container">
						<Table
							items={visibleAdmins}
							columns={columns}
							handleSort={handleSort}
							// Bearbeiten darf nur, wer die jeweilige Rolle verwalten
							// darf — sonst bleibt die Zeile Text.
							onRowClick={admin => openAdmin(admin)}
							rowTitle={t('update')}
						/>
						{/* UI/Table zeichnet bei einer leeren Liste nur den Kopf.
						    Ohne diesen Satz sähe ein Filter ohne Treffer wie ein
						    Fehler aus. */}
						{isFiltering && visibleAdmins.length === 0 && (
							<p className={styles.noMatch}>
								{t('No administrator matches this filter.')}{' '}
								<button type="button" className={styles.noMatchReset} onClick={handleResetFilter}>
									{t('Reset filters')}
								</button>
							</p>
						)}
					</div>
				</>
			) : (
				<p>{t('Loading permissions...')}</p>
			)}
		</div>
	)
}

const mapStateToProps = state => ({
	permissions: state.user.profile.permissions
})

export default connect(mapStateToProps)(Admins)
